import { z } from 'zod';

/** Runtime schemas and legacy normalization for Folio JSON payloads. */

export type ParsedFolioMode = 'guide' | 'assistant';

interface ParsedChartPoint {
  readonly x: string | number;
  readonly y: number;
}

interface ParsedChartReferenceLine {
  readonly y: number;
  readonly label?: string;
  readonly stroke?: string;
}

export interface ParsedChartData {
  readonly chart_type: 'bar' | 'line' | 'pie' | 'scatter' | 'histogram';
  readonly title: string;
  readonly x_label?: string;
  readonly y_label?: string;
  readonly series: readonly {
    readonly name: string;
    readonly data: readonly ParsedChartPoint[];
  }[];
  readonly reference_lines?: readonly ParsedChartReferenceLine[];
}

type ParsedTableCellValue = string | number | boolean | null;

export interface ParsedTableData {
  readonly columns: readonly string[];
  readonly rows: readonly (readonly ParsedTableCellValue[])[];
  readonly summary?: string;
  readonly title?: string;
}

export interface ParsedScalarData {
  readonly label: string;
  readonly value: string | number;
  readonly unit?: string;
  readonly change?: string;
  readonly context?: string;
  readonly tone?: 'positive' | 'negative' | 'neutral';
}

export const jsonValueSchema = z.json();
export const jsonObjectSchema = z.record(z.string(), jsonValueSchema);

export type JsonValue = z.infer<typeof jsonValueSchema>;
export type JsonObject = z.infer<typeof jsonObjectSchema>;

const folioModeSchema = z
  .enum(['guide', 'assistant', 'analyst'])
  .transform((mode): ParsedFolioMode => (mode === 'analyst' ? 'assistant' : mode));
const chartTypeSchema = z.enum(['bar', 'line', 'pie', 'scatter', 'histogram']);
const chartPointSchema = z
  .union([
    z.object({ x: z.union([z.string(), z.number()]), y: z.number() }),
    z.tuple([z.union([z.string(), z.number()]), z.number()]),
  ])
  .transform((point): ParsedChartPoint =>
    Array.isArray(point) ? { x: point[0], y: point[1] } : point,
  );
const chartReferenceLineSchema = z.object({
  y: z.number(),
  label: z.string().optional(),
  stroke: z.string().optional(),
}).transform((line): ParsedChartReferenceLine => ({
  y: line.y,
  ...(line.label === undefined ? {} : { label: line.label }),
  ...(line.stroke === undefined ? {} : { stroke: line.stroke }),
}));
const chartReferenceLinesSchema = z.array(chartReferenceLineSchema).min(1);
const chartSeriesSchema = z.object({
  name: z.string(),
  data: z.array(z.union([chartPointSchema, z.number()])),
});
const modernChartSchema = z.object({
  chart_type: chartTypeSchema,
  title: z.string(),
  x_label: z.string().optional(),
  y_label: z.string().optional(),
  xAxis: z.array(z.string()).optional(),
  series: z.array(chartSeriesSchema),
  reference_lines: jsonValueSchema.optional(),
});
const legacyChartSchema = z.object({
  type: chartTypeSchema,
  title: z.string(),
  xAxis: z.array(z.string()).optional(),
  series: z.array(z.object({ name: z.string(), data: z.array(z.number()) })),
});
const chartSchema = z.union([modernChartSchema, legacyChartSchema]);
const tableCellSchema = z.union([z.string(), z.number(), z.boolean(), z.null()]);
const tableRowSchema = z.union([
  z.array(tableCellSchema),
  z.record(z.string(), tableCellSchema),
]);
const tableSchema = z
  .object({
    columns: z.array(z.string()).min(1),
    rows: z.array(tableRowSchema),
    summary: z.string().optional(),
    title: z.string().optional(),
  })
  .superRefine((table, context) => {
    for (const row of table.rows) {
      if (Array.isArray(row) && row.length !== table.columns.length) {
        context.addIssue({ code: 'custom', message: 'Table rows must match the column count.' });
      }
    }
  });
const scalarSchema = z.object({
  label: z.string(),
  value: z.union([z.string(), z.number()]),
  unit: z.string().optional(),
  change: z.string().optional(),
  context: z.string().optional(),
  tone: z.enum(['positive', 'negative', 'neutral']).optional(),
});
const numberSchema = z.number();

function normalizeChartSeries(
  series: z.infer<typeof chartSeriesSchema>[],
  xAxis: string[] | undefined,
): ParsedChartData['series'] {
  return series.map((entry) => ({
    name: entry.name,
    data: entry.data.map((point, index) => {
      const numericPoint = numberSchema.safeParse(point);
      if (numericPoint.success) {
        return { x: xAxis?.[index] ?? index + 1, y: numericPoint.data };
      }
      const structuredPoint = chartPointSchema.safeParse(point);
      if (!structuredPoint.success) {
        throw new Error('Chart schema returned an invalid series point.');
      }
      return structuredPoint.data;
    }),
  }));
}

function normalizeLegacySeries(
  series: z.infer<typeof legacyChartSchema>['series'],
  xAxis: string[] | undefined,
): ParsedChartData['series'] {
  return series.map((entry) => ({
    name: entry.name,
    data: entry.data.map((y, index) => ({ x: xAxis?.[index] ?? index + 1, y })),
  }));
}

export function parseFolioMode(value: JsonValue | undefined): ParsedFolioMode | null {
  const result = folioModeSchema.safeParse(value);
  return result.success ? result.data : null;
}

export function parseStringValue(value: JsonValue | undefined): string | null {
  const result = z.string().safeParse(value);
  return result.success ? result.data : null;
}

export function formatFolioModeLabel(mode: ParsedFolioMode): string {
  switch (mode) {
    case 'guide':
      return 'Guide';
    case 'assistant':
      return 'Assistant';
  }
}

export function parseChartData(value: JsonValue | undefined): ParsedChartData | null {
  const result = chartSchema.safeParse(value);
  if (!result.success) {
    return null;
  }

  const chart = result.data;
  if ('type' in chart) {
    return {
      chart_type: chart.type,
      title: chart.title,
      series: normalizeLegacySeries(chart.series, chart.xAxis),
    };
  }

  const referenceLines = chartReferenceLinesSchema.safeParse(chart.reference_lines);
  return {
    chart_type: chart.chart_type,
    title: chart.title,
    series: normalizeChartSeries(chart.series, chart.xAxis),
    ...(chart.x_label === undefined ? {} : { x_label: chart.x_label }),
    ...(chart.y_label === undefined ? {} : { y_label: chart.y_label }),
    ...(referenceLines.success ? { reference_lines: referenceLines.data } : {}),
  };
}

export function parseTableData(value: JsonValue | undefined): ParsedTableData | null {
  const result = tableSchema.safeParse(value);
  if (!result.success) {
    return null;
  }

  const table = result.data;
  return {
    columns: table.columns,
    rows: table.rows.map((row) =>
      Array.isArray(row) ? row : table.columns.map((column) => row[column] ?? null),
    ),
    ...(table.summary === undefined ? {} : { summary: table.summary }),
    ...(table.title === undefined ? {} : { title: table.title }),
  };
}

export function parseScalarData(value: JsonValue | undefined): ParsedScalarData | null {
  const result = scalarSchema.safeParse(value);
  if (!result.success) {
    return null;
  }

  const scalar = result.data;
  return {
    label: scalar.label,
    value: scalar.value,
    ...(scalar.unit === undefined ? {} : { unit: scalar.unit }),
    ...(scalar.change === undefined ? {} : { change: scalar.change }),
    ...(scalar.context === undefined ? {} : { context: scalar.context }),
    ...(scalar.tone === undefined ? {} : { tone: scalar.tone }),
  };
}
